/**
 * Utility functions for parsing, incrementing, and comparing paper version numbers.
 * Version format: "major.minor" (e.g., "1.0", "2.1")
 */

export interface ParsedVersion {
  major: number;
  minor: number;
}

/**
 * Parse a version string into its major and minor components
 */
export function parseVersion(version: string): ParsedVersion {
  const parts = version.split('.');
  return {
    major: parseInt(parts[0], 10) || 1,
    minor: parseInt(parts[1], 10) || 0
  };
}

/**
 * Convert a parsed version back to a string
 */
export function versionToString(version: ParsedVersion): string {
  return `${version.major}.${version.minor}`;
}

/**
 * Increment the major version (e.g., 1.3 -> 2.0)
 * Used for significant changes like new methodology, major rewrites
 */
export function incrementMajorVersion(currentVersion: string): string {
  const parsed = parseVersion(currentVersion);
  return versionToString({
    major: parsed.major + 1,
    minor: 0
  });
}

/**
 * Increment the minor version (e.g., 1.3 -> 1.4)
 * Used for smaller updates like typo fixes, clarifications
 */
export function incrementMinorVersion(currentVersion: string): string {
  const parsed = parseVersion(currentVersion);
  return versionToString({
    major: parsed.major,
    minor: parsed.minor + 1
  });
}

/**
 * Compare two version strings
 * Returns: negative if a < b, positive if a > b, 0 if equal
 */
export function compareVersions(a: string, b: string): number {
  const parsedA = parseVersion(a);
  const parsedB = parseVersion(b);
  
  if (parsedA.major !== parsedB.major) {
    return parsedA.major - parsedB.major;
  }
  return parsedA.minor - parsedB.minor;
}

/**
 * Check if version A is newer than version B
 */
export function isNewerVersion(a: string, b: string): boolean {
  return compareVersions(a, b) > 0;
}

/**
 * Get both possible next versions for display
 */
export function getNextVersionOptions(currentVersion: string): {
  major: { version: string; label: string };
  minor: { version: string; label: string };
} {
  return {
    major: {
      version: incrementMajorVersion(currentVersion),
      label: `Major update (${incrementMajorVersion(currentVersion)}) - significant changes`
    },
    minor: {
      version: incrementMinorVersion(currentVersion),
      label: `Minor update (${incrementMinorVersion(currentVersion)}) - small fixes`
    }
  };
}
